<script setup lang="ts">
import useNotification from '@/composables/notification'

import LegacyRewardForm from './components/RewardForm.vue'
import NewRewardForm from '@/components/rewards/RewardForm.vue'

import type { LoyaltyReward } from '@/types/types'

import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import { normalizeMerchantRewardSavePayload } from '@/components/rewards/utils/rewardHelpers.js'
import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'
import { initializeRewardForm } from '@/components/rewards/utils/rewardFormInitialization'

const { t } = useI18n()
const route = useRoute()
const notification = useNotification()

const {
  currentReward,
  isLoading,
  loadRewardById,
  saveReward,
  isLoadingPartners,
  loadPartnerParameters,
  fetchRewardTypes,
  handleRewardUpdate,
  rewardTypeIdsBySlug
} = useRewardForm()

const { flags, pending: flagsPending } = useFeatureFlag()

const useNewRewardForm = computed(() => !!flags.value['offer-engine']?.enabled)

const RewardForm = computed(() => {
  return useNewRewardForm.value ? NewRewardForm : LegacyRewardForm
})

const rewardForm = ref<InstanceType<typeof NewRewardForm> | InstanceType<typeof LegacyRewardForm> | null>(null)

const initialReward = ref<LoyaltyReward | null>(null)

const pageTitle = computed(() => currentReward.value?.name || t('pages.reward.details.namePlaceholder'))

const parent = { label: t('pages.reward.title'), to: '/rewards' }

const hasChanges = computed(() => {
  return JSON.stringify(currentReward.value) !== JSON.stringify(initialReward.value)
})

const buildLegacyPayload = () => {
  if (!currentReward.value) return null

  const payload: any = {
    id: currentReward.value.id || null,
    rewardTypeId: currentReward.value.rewardType?.id || 1,
    name: currentReward.value.name,
    description: currentReward.value.description?.trim() || null,
    value: currentReward.value.value,
    externalId: currentReward.value.externalId,
    items: currentReward.value.items,
    source: currentReward.value.source || 'loyalty',

    voucherDiscountAmount: currentReward.value.voucherDiscountAmount || null,
    discountValuePercent: currentReward.value.discountValuePercent || null,
    minimumOrderAmount: currentReward.value.minimumOrderAmount || null
  }

  if (currentReward.value.minimumOrderAmount !== undefined) {
    payload.minimumOrderAmount = currentReward.value.minimumOrderAmount
  }

  if (currentReward.value.isForSingleUse !== undefined) {
    payload.isForSingleUse = currentReward.value.isForSingleUse
  }

  if (currentReward.value.pictureUrl !== initialReward.value?.pictureUrl) {
    payload.pictureUrl = currentReward.value.pictureUrl
  } else {
    delete payload.pictureUrl
  }

  if (flags.value['offer-engine']?.enabled) {
    normalizeMerchantRewardSavePayload(payload, currentReward.value)
  }

  return payload
}

const handleError = () => {
  const nameError = currentReward.value.name.length === 0

  if (nameError) {
    if (!useNewRewardForm.value) {
      ;(rewardForm.value as unknown as InstanceType<typeof LegacyRewardForm>)?.setNameError()
    }
  }

  return nameError
}

const handleSave = async () => {
  try {
    if (handleError()) return

    if (!currentReward.value) {
      notification.create('error', {
        text: t('pages.reward.errors.rewardNotFound')
      })
      return
    }

    if (useNewRewardForm.value) {
      await handleRewardUpdate(currentReward.value)
    } else {
      const payload = buildLegacyPayload()
      if (!payload) return

      await saveReward(payload)
    }

    await reloadReward(Number(route.params.rewardId))

    notification.create('success', {
      text: t('pages.reward.editReward.success')
    })
  } catch (error: unknown) {
    console.error(error)
    notification.create('error', {
      text: t('pages.reward.editReward.error')
    })
  }
}
const reloadReward = async (rewardId: number) => {
  const loaders: Promise<unknown>[] = [loadRewardById(rewardId)]
  if (useNewRewardForm.value) {
    loaders.push(loadPartnerParameters())
    loaders.push(fetchRewardTypes())
  }
  await Promise.all(loaders)

  if (useNewRewardForm.value && currentReward.value) {
    initializeRewardForm(currentReward.value, rewardTypeIdsBySlug.value)
  }

  initImages()

  initialReward.value = JSON.parse(JSON.stringify(currentReward.value))
}

const initImages = () => {
  if (!useNewRewardForm.value) {
    ;(rewardForm.value as unknown as InstanceType<typeof LegacyRewardForm>)?.resetImage()
  }
}

watch(
  () => [route.params.rewardId, flagsPending.value] as const,
  async ([rewardId, isFlagsPending]) => {
    if (isFlagsPending || !rewardId) return
    await reloadReward(Number(rewardId))
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="!flagsPending && !isLoading && !isLoadingPartners && currentReward">
    <div class="mx-auto max-w-7xl">
      <TheDetailHeader :title="pageTitle" :parent="parent">
        <template #actions>
          <Button variant="default-primary" :disabled="!hasChanges" @click="handleSave">
            {{ t('global.actions.save') }}
          </Button>
        </template>
      </TheDetailHeader>

      <div class="px-6 pb-6 sm:px-12 sm:pb-12">
        <div class="flex flex-col-reverse gap-6 lg:flex-row lg:gap-8">
          <RewardForm ref="rewardForm" v-model:editing-reward="currentReward" hide-claim-limit-block />
        </div>
      </div>
    </div>
  </div>

  <TheLoadingPage v-else />
</template>
